/**
 * The one screen an operator should be able to read in ten seconds.
 *
 * Ordered by what goes wrong: money owed first, then whether the economy is
 * behaving, then whether the data underneath it is fresh. Anything that needs
 * attention colours itself; everything else stays grey.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { api, type Overview } from '../lib/api'
import { ErrorNote, Loading, Panel, Stat, cents, dateTime, percent } from '../components/ui'

/** Prices older than this are stale enough to be an arbitrage on a live product. */
const STALE_HOURS = 36

export function OverviewPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<Overview>('/overview'),
    refetchInterval: 30_000,
  })

  if (isLoading) return <Loading />
  if (error || !data) return <ErrorNote>Could not load the overview.</ErrorNote>

  const { users, opens, liability, tiers, market } = data
  const stale = market ? market.ageHours > STALE_HOURS : false
  const floorBroken = market ? market.belowFloor > 0 : false

  return (
    <div className="space-y-5">
      {/* What the platform owes. On a simulated ledger this is the number that
          says how much real money would be needed to honour it. */}
      <Panel title="Outstanding">
        <div className="grid gap-6 p-5 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Total owed"
            value={cents(liability.outstandingCents, { compact: true })}
            sub="player balances + vault at market"
          />
          <Stat
            label="Cash balances"
            value={cents(liability.balancesCents, { compact: true })}
            sub="spendable right now"
          />
          <Stat
            label="Vault value"
            value={cents(liability.vaultCents, { compact: true })}
            sub={`${liability.positions.toLocaleString()} open positions`}
          />
          <Stat
            label="House margin"
            value={cents(opens.marginCents, { compact: true })}
            sub="staked − returned, at open time"
            tone={opens.marginCents >= 0 ? 'up' : 'down'}
          />
        </div>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel title="Economy" className="lg:col-span-2">
          <div className="grid gap-6 p-5 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Staked"
              value={cents(opens.stakedCents, { compact: true })}
              sub={`${cents(opens.staked24hCents, { compact: true })} in 24h`}
            />
            <Stat
              label="Returned"
              value={cents(opens.returnedCents, { compact: true })}
              sub={`${cents(opens.returned24hCents, { compact: true })} in 24h`}
            />
            <Stat
              label="Realised RTP"
              value={percent(opens.realisedRtp)}
              sub="against a published 54–63%"
              // Paying out more than the tables promise is the expensive
              // direction, so that is the one that shouts.
              tone={opens.realisedRtp !== null && opens.realisedRtp > 0.7 ? 'warn' : undefined}
            />
            <Stat
              label="Rips"
              value={opens.total.toLocaleString()}
              sub={`${opens.last24h.toLocaleString()} in 24h`}
            />
          </div>

          {opens.total < 200 && (
            <p
              className="border-t px-5 py-3 text-xs leading-relaxed"
              style={{ color: 'var(--color-faint)' }}
            >
              Realised RTP is drawn from {opens.total.toLocaleString()} rips. On a long-tailed
              payout that is far too few to read as anything — a single mythic moves it by tens of
              points. Treat it as noise until the count is in the thousands.
            </p>
          )}
        </Panel>

        <Panel title="Players">
          <div className="grid gap-6 p-5 sm:grid-cols-3 lg:grid-cols-1">
            <Stat label="Accounts" value={users.total.toLocaleString()} />
            <Stat label="New in 24h" value={users.new24h.toLocaleString()} />
            <Stat label="Active in 7d" value={users.active7d.toLocaleString()} sub="opened a pack" />
          </div>
        </Panel>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Stake by tier">
          {tiers.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-faint)' }}>
              Nothing opened yet.
            </div>
          ) : (
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th className="text-right">Rips</th>
                  <th className="text-right">Staked</th>
                  <th className="text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t) => {
                  const share = opens.stakedCents > 0 ? t.stakedCents / opens.stakedCents : 0
                  return (
                    <tr key={t.tier}>
                      <td className="font-semibold">{t.label}</td>
                      <td className="tnum text-right">{t.opens.toLocaleString()}</td>
                      <td className="tnum text-right">{cents(t.stakedCents, { compact: true })}</td>
                      <td className="tnum text-right" style={{ color: 'var(--color-muted)' }}>
                        {percent(share, 0)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel
          title="Market data"
          action={
            <Link to="/settlement" className="btn btn-ghost btn-sm">
              Settlement
            </Link>
          }
        >
          {!market ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-down)' }}>
              No market snapshot. Packs cannot be opened.
            </div>
          ) : (
            <>
              <div className="grid gap-6 p-5 sm:grid-cols-3">
                <Stat
                  label="Drawable"
                  value={market.drawable.toLocaleString()}
                  sub="tokens a pack can pay in"
                />
                <Stat
                  label="Priced"
                  value={`${market.ageHours.toFixed(0)}h ago`}
                  sub={dateTime(market.refreshedAt)}
                  tone={stale ? 'warn' : undefined}
                />
                <Stat
                  label="Below the floor"
                  value={market.belowFloor.toLocaleString()}
                  sub={`floor is $${(market.minMarketCap / 1e6).toFixed(0)}M`}
                  tone={floorBroken ? 'warn' : undefined}
                />
              </div>

              {stale && (
                <p
                  className="border-t px-5 py-3 text-xs leading-relaxed"
                  style={{ color: 'var(--color-warn)' }}
                >
                  Prices are {market.ageHours.toFixed(0)} hours old. Cards are priced off this
                  snapshot, so anything that has moved since is being sold at the wrong number —
                  run <code>market:refresh</code>.
                </p>
              )}

              {floorBroken && (
                <p
                  className="border-t px-5 py-3 text-xs leading-relaxed"
                  style={{ color: 'var(--color-warn)' }}
                >
                  {market.belowFloor} of {market.drawable} drawable assets sit below the $
                  {(market.minMarketCap / 1e6).toFixed(0)}M cap floor the pack pages publish. For
                  tokenized equities that figure measures how much has been minted rather than what
                  the company is worth, so the size guard is reading the wrong number for them.
                </p>
              )}
            </>
          )}
        </Panel>
      </div>
    </div>
  )
}
